import { MenuSelect } from "@/components/molecules/MenuSelect";
import { cn } from "@/lib/cn";
import {
  DEFAULT_RECURRING_SCHEDULE,
  HOUR_INTERVAL_OPTIONS,
  MINUTE_INTERVAL_OPTIONS,
  MONTH_DAY_OPTIONS,
  WEEKDAY_OPTIONS,
  cronToRecurringSchedule,
  describeRecurringSchedule,
  formatClock,
  recurringScheduleToCron,
  type RecurringKind,
  type RecurringSchedule,
} from "./campaignCron";

interface Props {
  cron: string;
  onChange: (cron: string) => void;
  error?: string;
}

const KIND_OPTIONS: { value: RecurringKind; label: string }[] = [
  { value: "minutes", label: "Every few minutes" },
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

function parseTime(value: string, fallback: RecurringSchedule): { hour: number; minute: number } {
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return { hour: fallback.hour, minute: fallback.minute };
  return {
    hour: Math.min(23, Number(match[1])),
    minute: Math.min(59, Number(match[2])),
  };
}

/**
 * Recurring schedule as everyday options (daily at 10:00, every Wednesday, …).
 * The value written is a 6-field cron expression for the campaign service.
 */
export function RecurringScheduleFields({ cron, onChange, error }: Props) {
  const parsed = cronToRecurringSchedule(cron);
  const schedule = parsed ?? DEFAULT_RECURRING_SCHEDULE;
  const unrecognized = Boolean(cron.trim()) && !parsed;

  const commit = (next: RecurringSchedule) => onChange(recurringScheduleToCron(next));

  const patch = (partial: Partial<RecurringSchedule>) => commit({ ...schedule, ...partial });

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
          Repeats
          <span className="ml-0.5 text-bridge-danger">*</span>
        </div>
        <MenuSelect
          hideLabel
          value={schedule.kind}
          onChange={(v) => patch({ kind: v as RecurringKind })}
          options={KIND_OPTIONS}
        />
      </div>

      {schedule.kind === "minutes" && (
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
            Every
          </div>
          <MenuSelect
            hideLabel
            value={String(schedule.interval)}
            onChange={(v) => patch({ interval: Number(v) })}
            options={MINUTE_INTERVAL_OPTIONS.map((n) => ({
              value: String(n),
              label: n === 1 ? "1 minute" : `${n} minutes`,
            }))}
          />
        </div>
      )}

      {schedule.kind === "hourly" && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
              Every
            </div>
            <MenuSelect
              hideLabel
              value={String(schedule.interval)}
              onChange={(v) => patch({ interval: Number(v) })}
              options={HOUR_INTERVAL_OPTIONS.map((n) => ({
                value: String(n),
                label: n === 1 ? "1 hour" : `${n} hours`,
              }))}
            />
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
              At minute
            </div>
            <MenuSelect
              hideLabel
              value={String(schedule.minute)}
              onChange={(v) => patch({ minute: Number(v) })}
              options={[0, 15, 30, 45].map((n) => ({
                value: String(n),
                label: `:${String(n).padStart(2, "0")}`,
              }))}
            />
          </div>
        </div>
      )}

      {(schedule.kind === "daily" || schedule.kind === "weekly" || schedule.kind === "monthly") && (
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
            At
          </div>
          <input
            type="time"
            value={formatClock(schedule.hour, schedule.minute)}
            onChange={(e) => patch(parseTime(e.target.value, schedule))}
            className="h-10 w-full rounded-md border border-bridge-border/70 bg-white px-3 text-sm text-bridge-fg focus-ring"
          />
        </div>
      )}

      {schedule.kind === "weekly" && (
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
            On
          </div>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_OPTIONS.map((day) => {
              const selected = schedule.weekdays.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  title={day.longLabel}
                  onClick={() => {
                    const next = selected
                      ? schedule.weekdays.filter((d) => d !== day.value)
                      : [...schedule.weekdays, day.value].sort((a, b) => a - b);
                    patch({ weekdays: next.length > 0 ? next : [day.value] });
                  }}
                  className={cn(
                    "h-9 min-w-[2.5rem] rounded-md border px-2 text-xs font-semibold transition-colors",
                    selected
                      ? "border-bridge-accent bg-bridge-accent text-white"
                      : "border-bridge-border/70 bg-white text-bridge-fg hover:bg-bridge-bg",
                  )}
                >
                  {day.label.slice(0, 3)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {schedule.kind === "monthly" && (
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
            Day of month
          </div>
          <MenuSelect
            hideLabel
            value={String(schedule.monthDay)}
            onChange={(v) => patch({ monthDay: Number(v) })}
            options={MONTH_DAY_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
          />
        </div>
      )}

      <p className="text-xs text-bridge-muted">{describeRecurringSchedule(schedule)}</p>
      {unrecognized && (
        <p className="text-xs text-amber-700">
          This campaign’s current schedule isn’t one of the presets. Choosing options above will
          replace it.
        </p>
      )}
      {error && <p className="text-xs text-bridge-danger">{error}</p>}
    </div>
  );
}
